import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { trackEvent, AnalyticsEvents } from '@/analytics';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { rehydratePendingInviteContext, logInviteStorageState } from '@/utils/inviteStorage';
import { InviteStorageHelper } from '@/utils/inviteStorageHelper';
import { processGoogleImageToCloudinary } from '@/utils/imageProcessing';
import { updateCachedImageUrl } from '@/utils/simpleImageCache';

/**
 * 🔗 V167.INVITE.AUTO: Generate unique invite code
 * Format: xxxxxxxx-xxxxxxxx-xxxx (e.g., ac930845-md3gxpaa-k4lf)
 */
function generateInviteCode(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';

  const randomString = (length: number) => {
    return Array.from({ length }, () =>
      chars[Math.floor(Math.random() * chars.length)]
    ).join('');
  };

  // Format: 8 chars - 8 chars - 4 chars
  const part1 = randomString(8);
  const part2 = randomString(8);
  const part3 = randomString(4);

  return `${part1}-${part2}-${part3}`;
}

interface OnboardingData {
  currentStep: number;
  data: any;
}

interface OnboardingContextType {
  data: any;
  updateData: (updates: any) => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  completeOnboarding: () => Promise<void>;
  currentStep: number;
  isSubmitting: boolean;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  // V149.9: Simplified onboarding - start at step 0 (BasicProfile)
  const getInitialStep = () => {
    const savedStep = localStorage.getItem('blissnet_onboarding_step');

    if (savedStep) {
      const step = parseInt(savedStep, 10);
      return Math.max(0, step); // Ensure step is at least 0
    }

    // Start at BasicProfileStep (step 0)
    return 0;
  };

  const [onboardingData, setOnboardingData] = useState<OnboardingData>({
    currentStep: getInitialStep(),
    data: {}
  });

  // Maintain a ref to always access the latest onboarding data snapshot
  const onboardingDataRef = useRef(onboardingData);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Prevent step regression during auth transitions
  const [isAuthTransition, setIsAuthTransition] = useState(false);

  // Monitor for auth completion and prevent step regression
  useEffect(() => {
    const authCompleted = localStorage.getItem('blissnet_auth_completed');
    const savedStep = localStorage.getItem('blissnet_onboarding_step');

    if (authCompleted === 'true' && savedStep) {
      const targetStep = parseInt(savedStep, 10);

      // If we're not at the correct step after auth, fix it
      if (onboardingData.currentStep !== targetStep && targetStep >= 3) {
        setIsAuthTransition(true);
        setOnboardingData(prev => ({
          ...prev,
          currentStep: targetStep
        }));

        // Clear transition flag after a short delay
        setTimeout(() => {
          setIsAuthTransition(false);
        }, 1000);
      }
    } else {
      // Ensure we're not stuck in auth transition for new users
      setIsAuthTransition(false);
    }
  }, []); // Run once on mount

  useEffect(() => {
    onboardingDataRef.current = onboardingData;
  }, [onboardingData]);

  // V167.INVITE.FALLBACK: Fetch full invite context if we only have invite code
  useEffect(() => {
    async function loadInviteContext() {
      console.log('🔍 V167.INVITE.FALLBACK: Checking for invite code in onboarding');

      // Get invite code from storage
      const inviteCode = localStorage.getItem('pendingInviteCode') ||
        sessionStorage.getItem('pendingInviteCode');

      if (!inviteCode) {
        console.log('⚠️ V167.INVITE.FALLBACK: No invite code found');
        return;
      }

      console.log('✅ V167.INVITE.FALLBACK: Found invite code:', inviteCode.substring(0, 8) + '...');

      // Check if we already have full context
      const existingContext = localStorage.getItem('pendingInviteContext') ||
        sessionStorage.getItem('pendingInviteContext');
      if (existingContext) {
        console.log('✅ V167.INVITE.FALLBACK: Context already loaded');
        return;
      }

      // V300: Fetch invite data using RPC (RLS-compliant)
      console.log('🔍 V300: Validating invite via RPC...');
      try {
        const { data: validationResult, error } = await supabase
          .rpc('validate_invite_code', { p_code: inviteCode });

        if (error) {
          console.error('❌ V300: Error validating invite:', error);
          return;
        }

        const result = Array.isArray(validationResult) ? validationResult[0] : validationResult;

        if (!result || !result.valid) {
          console.error('❌ V300: Invite validation failed:', result?.error_message);
          return;
        }

        const { rpcPeerUsersForViewer } = await import('@/lib/d001PeerUsersRpc');
        const { data: creatorRows } = await rpcPeerUsersForViewer(
          supabase,
          [result.creator_id],
          { includePrefs: false, inviteCode: inviteCode }
        );
        const creatorData = creatorRows?.[0];

        console.log('✅ V300: Invite validated:', {
          inviteCode: inviteCode.substring(0, 8) + '...',
          creatorId: result.creator_id,
          creatorName: result.creator_name
        });

        // Build data object matching old structure
        const data = {
          invite_code: inviteCode,
          creator_id: result.creator_id,
          created_at: new Date().toISOString(), // Not available from RPC
          creator: creatorData || { id: result.creator_id, name: result.creator_name, photo_urls: [] }
        };

        // Build full context
        const inviteContext = {
          inviteCode: data.invite_code,
          inviterId: data.creator_id,
          inviterName: data.creator?.name || 'Someone',
          inviterAvatar: data.creator?.photo_urls?.[0] || '',
          clickedAt: new Date().toISOString()
        };

        // Store full context in BOTH storages
        const contextStr = JSON.stringify(inviteContext);
        localStorage.setItem('pendingInviteContext', contextStr);
        sessionStorage.setItem('pendingInviteContext', contextStr);
        localStorage.setItem('inviteChoice', 'connect');
        sessionStorage.setItem('inviteChoice', 'connect');

        console.log('✅ V167.INVITE.FALLBACK: Full context stored in both storages');

      } catch (error) {
        console.error('❌ V167.INVITE.FALLBACK: Exception:', error);
      }
    }

    loadInviteContext();
  }, []); // Run once on mount

  const updateData = (updates: any) => {
    setOnboardingData(prev => {
      const newState = {
        ...prev,
        data: { ...prev.data, ...updates }
      };
      // V241: CRITICAL - Update ref synchronously to ensure completeOnboarding sees latest data
      onboardingDataRef.current = newState;
      return newState;
    });
  };

  const nextStep = () => {
    console.log('[V149.10] nextStep called, current:', onboardingData.currentStep);

    // Prevent step changes during auth transitions
    if (isAuthTransition) {
      console.log('[V149.10] Blocked by auth transition');
      return;
    }

    setOnboardingData(prev => {
      const newStep = prev.currentStep + 1;
      console.log('[V149.10] Advancing from', prev.currentStep, 'to', newStep);

      // Save current step to localStorage for recovery
      localStorage.setItem('blissnet_onboarding_step', newStep.toString());

      return {
        ...prev,
        currentStep: newStep
      };
    });
  };

  const prevStep = () => {
    setOnboardingData(prev => ({
      ...prev,
      currentStep: Math.max(1, prev.currentStep - 1)
    }));
  };

  const goToStep = (step: number) => {
    // Prevent step changes during auth transitions
    if (isAuthTransition) {
      return;
    }

    setOnboardingData(prev => {
      // Save current step to localStorage for recovery
      localStorage.setItem('blissnet_onboarding_step', step.toString());

      return {
        ...prev,
        currentStep: step
      };
    });
  };

  const completeOnboarding = async () => {
    console.log('🚀🚀🚀 V167.PUSH.FIX3: completeOnboarding() CALLED - NEW CODE LOADED');
    console.log('🚀🚀🚀 V167.PUSH.FIX3: Timestamp:', new Date().toISOString());

    try {
      setIsSubmitting(true);

      // Get current authenticated user
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error('No authenticated user found');
      }

      // V152.7: FIXED - Helper function to get photo URL from correct sources
      const currentOnboardingState = onboardingDataRef.current;
      const currentData = currentOnboardingState?.data ?? {};

      const getPhotoUrl = () => {
        // Priority 1: New photoUrls array from PhotosStep (V151.19)
        if (currentData.photoUrls && currentData.photoUrls.length > 0) {
          console.log('✅ V152.7: Found photo URL in onboarding data:', currentData.photoUrls[0]);
          return currentData.photoUrls[0];
        }

        // Priority 2: Legacy server uploaded photo
        if (currentData.profile?.hasUploadedPhoto && currentData.profile?.photoUrl) {
          console.log('✅ V152.7: Found legacy photo URL:', currentData.profile.photoUrl);
          return currentData.profile.photoUrl;
        }

        // Priority 3: Local photo from localStorage (fallback)
        try {
          const localPhoto = localStorage.getItem(`blissnet_selfie_${user.id}`);
          if (localPhoto) {
            const photoData = JSON.parse(localPhoto);
            console.log('✅ V152.7: Found localStorage photo URL');
            return photoData.dataUrl;
          }
        } catch (error) {
          console.log('⚠️ V152.7: No localStorage photo found');
        }

        // Priority 4: Google OAuth avatar (V206.15 - high-res)
        const avatarUrl = user.user_metadata?.avatar_url;
        if (avatarUrl?.includes('googleusercontent.com')) {
          const highResUrl = avatarUrl.split('=s')[0] + '=s2048';
          console.log('📸 V206.15: Using high-res Google photo', {
            original: avatarUrl,
            highRes: highResUrl
          });
          return highResUrl;
        } else if (avatarUrl) {
          console.log('📸 V206.15: Using OAuth avatar:', avatarUrl);
          return avatarUrl;
        }

        console.log('⚠️ V152.7: No photo URL found in any source');
        return null;
      };

      // V242: Debug onboarding data structure with detailed logging
      console.log('🔍 V242: Complete onboarding - currentData:', currentData);
      console.log('🔍 V242: Complete onboarding - currentData.photoUrls:', currentData.photoUrls);
      console.log('🔍 V242: Complete onboarding - currentData.photoUrls type:', typeof currentData.photoUrls);
      console.log('🔍 V242: Complete onboarding - currentData.photoUrls is array:', Array.isArray(currentData.photoUrls));
      console.log('🔍 V242: Complete onboarding - currentData.photoUrls length:', currentData.photoUrls?.length);
      if (currentData.photoUrls && currentData.photoUrls.length > 0) {
        console.log('🔍 V242: Complete onboarding - first photoUrl:', currentData.photoUrls[0]);
        console.log('🔍 V242: Complete onboarding - first photoUrl type:', typeof currentData.photoUrls[0]);
        console.log('🔍 V242: Complete onboarding - first photoUrl length:', currentData.photoUrls[0]?.length);
      }
      console.log('🔍 V242: Complete onboarding - Profile in data:', currentData.profile);

      const photoUrl = getPhotoUrl();
      const userEmail = currentData.email || user.email || '';

      if (!userEmail) {
        throw new Error('Authenticated user email missing during onboarding completion');
      }
      console.log('🔍 V242: Final photo URL for database (from getPhotoUrl):', photoUrl);
      console.log('🔍 V242: Final photo URL type:', typeof photoUrl);
      console.log('🔍 V242: Final photo URL is truthy:', !!photoUrl);

      // Check if user already exists
      const { data: existingUser, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw new Error(`Failed to fetch existing user data: ${fetchError.message}`);
      }

      if (existingUser) {
        // User exists, update with onboarding data
        // V156.2.2: CRITICAL FIX - Don't overwrite photo_urls if already set by upload service
        const updateData: any = {
          name: currentData.name,
          email: userEmail,
          bio: currentData.bio,
          date_of_birth: currentData.date_of_birth || currentData.dateOfBirth || existingUser?.date_of_birth || null,
          gender: currentData.gender,
          trust_score: 0, // Default trust score for new users (10 points per friend + 1 per friend-of-friend)
          is_verified: false,
          updated_at: new Date().toISOString()
        };

        console.log('🚀 OnboardingContext: Updating existing user with data:', JSON.stringify(updateData, null, 2));
        console.log('🚀 OnboardingContext: Name being saved:', updateData.name);
        console.log('🚀 OnboardingContext: User ID:', user.id);

        // V156.2.2: Only update photo_urls if we have a new photo AND existing user doesn't have photos
        // V213.5: Sanitize photo_urls to prevent blob:/data: URLs, and guard against overwriting existing Cloudinary photos
        if (photoUrl && (!existingUser.photo_urls || existingUser.photo_urls.length === 0)) {
          // V213.5: Sanitize to ensure no blob URLs
          const { sanitizeUserUpdate, hasRealCloudinaryPhoto } = await import('../utils/sanitizeUserUpdate');
          const sanitized = sanitizeUserUpdate({ photo_urls: [photoUrl] });
          if (sanitized.photo_urls && sanitized.photo_urls.length > 0) {
            updateData.photo_urls = sanitized.photo_urls;
            console.log('✅ V156.2.2: Adding photo_urls to update (user had no photos)');
            // V242.1: Also add publicId if available
            if (currentData.photoPublicId) {
              updateData.profile_image_public_id = currentData.photoPublicId;
              console.log('✅ V242.1: Adding profile_image_public_id to update:', currentData.photoPublicId);
            }
          } else {
            console.warn('⚠️ V213.5: photo_urls was filtered out (contained blob/data URL) - skipping photo update');
          }
        } else if (photoUrl && existingUser.photo_urls && existingUser.photo_urls.length > 0) {
          // V213.5: Additional guard - don't overwrite if user has real Cloudinary photo
          const { hasRealCloudinaryPhoto } = await import('../utils/sanitizeUserUpdate');
          if (hasRealCloudinaryPhoto(existingUser)) {
            console.log('⚠️ V213.5: Skipping photo_urls update (user already has Cloudinary photo)');
          } else {
            console.log('⚠️ V156.2.2: Skipping photo_urls update (user already has photos from upload service)');
          }
        }

        // V213.5: Sanitize entire updateData before sending to DB
        const { sanitizeUserUpdate: sanitizeAll } = await import('../utils/sanitizeUserUpdate');
        const finalUpdateData = sanitizeAll(updateData);

        console.log('📝 OnboardingContext: Final sanitized data:', JSON.stringify(finalUpdateData, null, 2));

        const { error: updateError, data: updateResult } = await supabase
          .from('users')
          .update(finalUpdateData)
          .eq('id', user.id)
          .select('id, name, email, updated_at')
          .single();

        if (updateError) {
          console.error('❌ OnboardingContext: Error updating existing user:', updateError);
          console.error('❌ OnboardingContext: Error details:', JSON.stringify(updateError, null, 2));
          throw new Error('Failed to update user profile');
        }

        console.log('✅ OnboardingContext: User updated successfully');
        console.log('✅ OnboardingContext: Updated user data:', JSON.stringify(updateResult, null, 2));
      } else {
        // New user, insert
        // V207.21: Process Google image through Cloudinary for actual 5:4 WebP with userName
        let finalPhotoUrl = photoUrl;
        if (photoUrl?.includes('googleusercontent.com')) {
          console.log('🖼️ V207.21: Starting Cloudinary processing for Google image');
          console.log('📍 V207.21: Photo URL:', photoUrl);
          console.log('📍 V207.21: User ID:', user.id);
          console.log('📍 V207.21: User Name:', user.user_metadata?.name);

          try {
            // V207.21: Process with userName for Cloudinary display name
            const cloudinaryUrl = await processGoogleImageToCloudinary(
              photoUrl,
              user.id,
              user.user_metadata?.name || user.user_metadata?.full_name || undefined
            );

            console.log('📍 V207.21: Processing result:', cloudinaryUrl);

            if (cloudinaryUrl && cloudinaryUrl.includes('cloudinary.com')) {
              finalPhotoUrl = cloudinaryUrl;
              console.log('✅ V206.22: Using Cloudinary 5:4 WebP image:', finalPhotoUrl);

              // V206.22: Update both caches immediately for instant display
              try {
                // Update simpleImageCache (used by UserPhoto components)
                updateCachedImageUrl(user.id, cloudinaryUrl);
                console.log('✅ V206.22: simpleImageCache updated');

                // Dispatch event for PhotoContext
                window.dispatchEvent(new CustomEvent('photoUpdated', {
                  detail: { userId: user.id, photoUrl: cloudinaryUrl }
                }));
                console.log('✅ V206.22: PhotoContext cache update event dispatched');
              } catch (cacheError) {
                console.warn('⚠️ V206.22: Failed to update caches:', cacheError);
              }
            } else {
              // Fallback to high-res Google URL
              finalPhotoUrl = photoUrl.split('=s')[0] + '=s2048';
              console.log('⚠️ V206.22: Using high-res Google fallback:', finalPhotoUrl);
            }
          } catch (processingError) {
            console.error('❌ V206.22: Processing error:', processingError);
            // Fallback to high-res Google URL
            finalPhotoUrl = photoUrl.split('=s')[0] + '=s2048';
            console.log('⚠️ V206.22: Using high-res Google fallback after error:', finalPhotoUrl);
          }
        }

        // V242: Log final photo URL before INSERT
        console.log('V242_ONBOARD_INSERT_START: Preparing new user insert');
        console.log('✅ V242: finalPhotoUrl value:', finalPhotoUrl);
        console.log('✅ V242: finalPhotoUrl is truthy:', !!finalPhotoUrl);
        console.log('✅ V242: photo_urls array will be:', finalPhotoUrl ? [finalPhotoUrl] : []);
        console.log('✅ V242.1: photoPublicId from onboarding data:', currentData.photoPublicId);

        // V272.6: For new users, prepare userData for edge function
        const userData: Record<string, any> = {
          name: currentData.name,
          date_of_birth: currentData.date_of_birth || currentData.dateOfBirth || null,
          photo_urls: finalPhotoUrl ? [finalPhotoUrl] : [],
          bio: currentData.bio,
          gender: currentData.gender
        };

        // V242.1: Add profile_image_public_id if we have it from PhotosStep
        if (currentData.photoPublicId) {
          userData.profile_image_public_id = currentData.photoPublicId;
          console.log('✅ V242.1: Adding profile_image_public_id:', currentData.photoPublicId);
        }

        console.log('[V272.6] Onboarding: Calling edge function:', {
          userId: user.id,
          userEmail,
          hasPhotoUrl: userData.photo_urls?.length > 0,
          hasPublicId: !!userData.profile_image_public_id
        });

        // V272.6: Call edge function instead of direct upsert (bypasses RLS)
        const { data, error } = await supabase.functions.invoke('ensure-user-row', {
          body: {
            userId: user.id,
            userEmail: userEmail,
            userData: userData
          }
        });

        if (error) {
          console.error('[V272.6] ❌ ensure-user-row failed:', error);
          throw new Error(`Failed to create/update user: ${error.message}`);
        }

        if (!data?.user) {
          console.error('[V272.6] ❌ Edge function succeeded but returned no user');
          throw new Error('Edge function succeeded but returned no user');
        }

        console.log('[V272.6] ✅ User created via edge function:', {
          userId: data.user.id,
          hasPhotoUrl: !!finalPhotoUrl,
          hasPublicId: !!currentData.photoPublicId
        });
      }

      // V314.6: Profile creation now handled by DB trigger (no app-side creation needed)
      // See migration: 20260130_v3146_profiles_trigger.sql
      console.log('✅ V314.6: Profile creation handled by DB trigger (no app action needed)');

      // 🔗 V177: Auto-generate invite link for new user (using template system)
      console.log('🔗 V177: Generating unique invite link for user');

      try {
        const { generalInvitationService } = await import('@/services/friends/generalInvitationService');

        // FIX: Cast to 'any' to bypass the missing type definition error
        const createResult = await (generalInvitationService as any).createGeneralInvitation(user.id);

        if (!createResult.success) {
          console.error('⚠️ V177: Error creating invite link:', createResult.error);
        } else {
          console.log('✅ V177: Invite link created successfully:', createResult.inviteCode);
          console.log('🔗 V177: Link:', createResult.inviteLink);
        }
      } catch (error) {
        console.error('⚠️ V177: Exception creating invite link:', error);
      }
      // Mark onboarding as completed (both global and per-user)
      localStorage.setItem('blissnet_onboarding_completed', 'true');
      localStorage.setItem(`onboarding_completed_${user.id}`, 'true');

      // V167.AUDIT: Set quick_onboarding_completed_at (one-time, only if NULL)
      // TODO: CTO will add column via migration — ALTER TABLE users ADD COLUMN IF NOT EXISTS quick_onboarding_completed_at TIMESTAMPTZ;
      try {
        const { error: qocError } = await supabase
          .from('users')
          .update({ quick_onboarding_completed_at: new Date().toISOString() })
          .eq('id', user.id)
          .is('quick_onboarding_completed_at', null);
        if (qocError) {
          console.warn('⚠️ V167.AUDIT: Could not set quick_onboarding_completed_at (column may not exist yet):', qocError.message);
        } else {
          console.log('✅ V167.AUDIT: quick_onboarding_completed_at set for user', user.id);
        }
      } catch (qocErr) {
        console.warn('⚠️ V167.AUDIT: quick_onboarding_completed_at update failed (non-blocking):', qocErr);
      }

      // 🎯 V167.INVITE.HELPER: NEW INVITATION PROCESSING - Show popup instead of automatic processing
      console.log('🔍 V167.INVITE.HELPER: Checking for invite context after onboarding');
      InviteStorageHelper.debugPrintStorage();

      // V167.INVITE: Use centralized helper to get invite context
      const inviteContext = InviteStorageHelper.getInviteContext();

      if (inviteContext) {
        console.log('✅ V167.INVITE.HELPER: Found invite context, preparing popup');

        // V167.INVITE: Prepare popup data with correct structure
        const popupData = {
          inviteCode: inviteContext.inviteCode,
          inviterId: inviteContext.inviterId,
          inviterName: inviteContext.inviterName,
          inviterAvatar: inviteContext.inviterAvatar,
          userId: user.id
        };

        console.log('🎯 V167.INVITE.HELPER: Storing popup data:', {
          inviteCode: popupData.inviteCode.substring(0, 8) + '...',
          inviterId: popupData.inviterId,
          inviterName: popupData.inviterName,
          userId: popupData.userId
        });

        // V167.INVITE: Use helper to store popup data (stores in both storages)
        InviteStorageHelper.storePopupData(popupData);

        console.log('✅ V167.INVITE.HELPER: Popup data stored, will show on Profile page');

        // Verify storage
        const { shouldShow, data } = InviteStorageHelper.getPopupData();
        console.log('✅ V167.INVITE.HELPER: Verification - shouldShow:', shouldShow, 'hasData:', !!data);

      } else {
        console.log('🔍 V167.INVITE.HELPER: No invitation to process');
      }

      // V145.4: NUCLEAR CACHE PRESERVATION - Ensure photo cache survives page reload
      console.log('💾 V145.4: Preserving photo cache before page reload...');

      // V208.0: Analytics - onboarding_completed
      trackEvent(AnalyticsEvents.ONBOARDING_COMPLETED).catch(() => {});

      // Trigger a final cache save by dispatching a custom event
      window.dispatchEvent(new CustomEvent('onboardingComplete', {
        detail: { userId: user.id, preserveCache: true }
      }));

      // Small delay to ensure cache is saved
      await new Promise(resolve => setTimeout(resolve, 100));

      // V167.INVITE: DO NOT clean up invitation data here - CompletionScreen needs it!
      // Only clean up onboarding-specific data
      localStorage.removeItem('blissnet_auth_completed');
      localStorage.removeItem('blissnet_onboarding_step');

      // V167.INVITE: Keep inviteChoice and pendingInviteContext for CompletionScreen
      const inviteChoice = localStorage.getItem('inviteChoice') ||
        sessionStorage.getItem('inviteChoice');
      const pendingInviteContext = localStorage.getItem('pendingInviteContext') ||
        sessionStorage.getItem('pendingInviteContext');
      console.log('✅ V167.INVITE: Keeping invitation data for CompletionScreen');
      console.log('✅ V167.INVITE: inviteChoice =', inviteChoice);
      console.log('✅ V167.INVITE: hasPendingContext =', !!pendingInviteContext);

      console.log('🔄 V145.4: Onboarding complete, CompletionScreen will handle navigation');

      // V167.INVITE: DO NOT redirect here - let CompletionScreen handle it
      // The CompletionScreen will show the invitation popup BEFORE navigating
      console.log('✅ V167.INVITE: CompletionScreen will show popup and then navigate');

      // Navigate directly to profile (splash screen will appear during route transition)
      window.location.replace('/profile');

    } catch (error) {
      toast.error('Failed to complete setup. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OnboardingContext.Provider value={{
      data: onboardingData.data,
      updateData,
      nextStep,
      prevStep,
      goToStep,
      completeOnboarding,
      currentStep: onboardingData.currentStep,
      isSubmitting
    }}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding(): OnboardingContextType {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context as OnboardingContextType;
}

